import { useContext } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { HouseholdsController } from "../../../api/src/households/households.controller";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import AllocationBar from "../components/AllocationBar/AllocationBar";
import AssetAllocationBarChart, {
  AssetAllocationSeries,
} from "../components/AssetAllocationBarChart";
import LabelValuePair from "../components/LabelValuePair";
import { StrategyBadge } from "../components/ModelSourceBadge";
import { useAuthenticatedFetch } from "../lib/api";
import {
  deserializeAllocationDetailsPercents,
  useAuthenticatedPercentFetch,
} from "../clients/percents";
import { naLabel, pendingLabel } from "../lib/display";
import { hasAccountsWithoutModels } from "../lib/modelValidation";
import { formatCurrency, formatPercent } from "../lib/numbers";
import { calculateHouseholdExcludedSecuritiesPercentage } from "../lib/excludedSecurities";
import { calculatePositionsCash } from "../lib/security";
import { useQueryModel } from "../models/lib";
import RebalanceHoldings from "./RebalanceHoldings";
import { RebalanceContext } from "./RebalanceInfo";
import {
  getRealizedGainLoss,
  getRiskAllocation,
  rebalanceTypeDisplay,
} from "./lib";

const RebalanceSummary = () => {
  const { rebalance, household } = useContext(RebalanceContext);
  const trades = rebalance?.accounts.flatMap((account) => account.trades) ?? [];

  const allHoldings =
    household?.accounts?.flatMap((account) => account.holdings ?? []) ?? [];
  const settledCash = calculatePositionsCash(allHoldings);
  const estimatedUnsettledCash = household?.cashBalance ?? 0;

  const {
    isLoading: isLoadingModel,
    isError: isErrorModel,
    data: dataModel,
  } = useQueryModel(household?.assignedModelId ?? -1, {
    enabled:
      typeof household !== "undefined" && household.assignedModelId != null,
  });

  const {
    isLoading: isLoadingHouseholdAllocation,
    isError: isErrorHouseholdAllocation,
    data: dataHouseholdAllocation,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<HouseholdsController["getHouseholdAllocationDetails"]>
  >(
    `/v1/households/${household?.id}/allocation-details`,
    deserializeAllocationDetailsPercents,
    undefined,
    { enabled: typeof household !== "undefined" },
  );

  const { data: explainData } = useAuthenticatedFetch<
    UnpackResponse<RebalancesController["getRebalanceExplain"]>
  >(`/rebalances/${rebalance?.id}/explain`, undefined, {
    enabled: typeof rebalance !== "undefined",
  });

  // Find rebalance reason and hint from propose_rebalance step
  const proposeRebalanceStep = explainData?.data?.find(
    (step) => step.stepName === "propose_rebalance",
  );
  // `explainData` is the step's JSON as the python worker wrote it.
  const proposeRebalanceData = proposeRebalanceStep?.explainData as
    { rebalance_reason?: string; rebalance_hint?: string } | null | undefined;
  const rebalanceReason = proposeRebalanceData?.rebalance_reason;
  const rebalanceHint = proposeRebalanceData?.rebalance_hint;

  // Check if household has accounts without models (excluding $0 AUM accounts)
  const accountsWithoutModels = hasAccountsWithoutModels(
    household,
    household?.accounts ?? [],
  );

  // Determine if we can show target allocation:
  // - All accounts have models, OR
  // - Using completion strategy (calculates targets for accounts without models)
  const isCompletionStrategy =
    household?.modelAssignmentStrategy === "completion";
  const canShowTargetAllocation =
    !accountsWithoutModels || isCompletionStrategy;

  // Calculate percentage of excluded securities across all household accounts
  const excludedSecuritiesPercentage = household?.accounts
    ? calculateHouseholdExcludedSecuritiesPercentage(household.accounts)
    : 0;

  const assetAllocationSeries: AssetAllocationSeries[] = [
    {
      label: "Current",
      data: dataHouseholdAllocation?.data.current ?? [],
    },
    {
      label: "Post-Rebalance",
      data: rebalance?.assetAllocation ?? [],
    },
    ...(canShowTargetAllocation
      ? [
          {
            label: "Target",
            // Prefer the household model's target directly so residual
            // accounts (which inherit per-account targets in the aggregate)
            // do not skew the household-level target allocation.
            data:
              dataHouseholdAllocation?.data.householdModelTarget ??
              dataHouseholdAllocation?.data.target ??
              [],
          },
        ]
      : []),
  ];

  const buyTotal = trades
    .filter((trade) => trade.side === "buy")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const sellTotal = trades
    .filter((trade) => trade.side === "sell" || trade.side === "sell_all")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const realizedGainLoss = rebalance ? getRealizedGainLoss(rebalance) : null;

  const currencyLabelValueProps = {
    orientation: "horizontal" as "horizontal" | "vertical",
    widths: { label: { lg: 3, xs: 12 }, value: { lg: 4 } },
    classNameValue: "text-lg-end",
    plaintext: true,
  };

  return typeof rebalance === "undefined" ? (
    <Loading />
  ) : (
    <Row>
      {accountsWithoutModels &&
        household?.modelAssignmentStrategy !== "completion" && (
          <Col xs={12} className="mb-3">
            <Alert variant="secondary" className="py-2 px-3 mb-2">
              Target Allocation requires a household model or account models for
              every account.
            </Alert>
          </Col>
        )}
      {household?.modelAssignmentStrategy === "completion" &&
        accountsWithoutModels && (
          <Col xs={12} className="mb-3">
            <Alert variant="info" className="py-2 px-3 mb-2">
              <strong>Using Completion Strategy</strong> - Accounts without
              models receive calculated targets to achieve household allocation.
            </Alert>
          </Col>
        )}
      <Col xl={6} className="mb-3">
        <LabelValuePair
          label="Rebalance Reason"
          value={`${rebalanceTypeDisplay[rebalance.type]}${rebalanceReason ? ` - ${rebalanceReason}` : ""}`}
          orientation="horizontal"
          widths={{ label: { xl: 4, lg: 3 } }}
          plaintext
        />
        {(rebalanceReason || rebalanceHint) && (
          <LabelValuePair
            label={` `}
            value={rebalanceHint}
            orientation="horizontal"
            widths={{ label: { xl: 4, lg: 3 } }}
            plaintext
            className="mb-3"
          />
        )}
        <LabelValuePair
          label="Current Model"
          value={
            isLoadingModel ? (
              <Loading />
            ) : isErrorModel ? (
              <span className="text-danger">Failed to load model</span>
            ) : typeof dataModel === "undefined" ? (
              naLabel
            ) : (
              <>
                <Link to={`/models/models/${dataModel.id}`}>
                  {dataModel.name}
                </Link>
                {household?.modelAssignmentStrategy && (
                  <span className="ms-2">
                    <StrategyBadge
                      strategy={household.modelAssignmentStrategy}
                    />
                  </span>
                )}
              </>
            )
          }
          orientation="horizontal"
          widths={{ label: { xl: 4, lg: 3 } }}
          plaintext
        />
        <LabelValuePair
          label="AUM"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(household?.householdBalance ?? 0, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Est Unsettled Cash"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(estimatedUnsettledCash, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Settled Cash"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(settledCash, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Buy $"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(buyTotal, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Sell $"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(sellTotal, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Net $"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(sellTotal - buyTotal, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Realized Gains"
          value={
            rebalance.status === "requested"
              ? pendingLabel
              : formatCurrency(realizedGainLoss, 2)
          }
          {...currencyLabelValueProps}
        />
        <LabelValuePair
          label="Current Allocation"
          value={
            <AllocationBar
              data={rebalance.riskAllocation?.current}
              converter={getRiskAllocation}
            />
          }
          orientation="horizontal"
          widths={{ label: { xl: 4, lg: 3 }, value: { xl: 6, lg: 6 } }}
          plaintext
        />
        <LabelValuePair
          label="Post-Rebalance Allocation"
          value={
            rebalance.status === "requested" ? (
              pendingLabel
            ) : (
              <AllocationBar
                data={rebalance.riskAllocation?.postRebalance}
                converter={getRiskAllocation}
              />
            )
          }
          orientation="horizontal"
          widths={{ label: { xl: 4, lg: 3 }, value: { xl: 6, lg: 6 } }}
          plaintext
        />
        {canShowTargetAllocation && (
          <LabelValuePair
            label="Target Allocation"
            value={
              rebalance.status === "requested" ? (
                pendingLabel
              ) : (
                <AllocationBar
                  data={rebalance.riskAllocation?.target}
                  converter={getRiskAllocation}
                />
              )
            }
            orientation="horizontal"
            widths={{ label: { xl: 4, lg: 3 }, value: { xl: 6, lg: 6 } }}
            plaintext
          />
        )}
        {canShowTargetAllocation && excludedSecuritiesPercentage > 0 && (
          <div className="text-muted small fst-italic mt-2">
            *Excluded securities (
            {formatPercent(excludedSecuritiesPercentage / 100, 0)} of holdings)
            are not calculated in the target allocation.
          </div>
        )}
      </Col>
      <Col xl={6}>
        {isLoadingHouseholdAllocation ? (
          <Loading />
        ) : isErrorHouseholdAllocation ? (
          <Alert variant="danger">Failed to load asset allocation</Alert>
        ) : (
          <AssetAllocationBarChart
            data={assetAllocationSeries}
            excludedSecuritiesPercentage={excludedSecuritiesPercentage}
          />
        )}
      </Col>
      <Col xs={12}>
        <RebalanceHoldings />
      </Col>
    </Row>
  );
};

export default RebalanceSummary;
